import React, { useState, useEffect } from 'react';
import { DefaultPageLayout } from '../components/layouts/DefaultPageLayout';
import { Button } from '../components/ui/button';
import { IconWithBackground } from '../components/ui/icon-with-background';
import BunnyLoader from '../components/ui/BunnyLoader';
import { 
  FeatherCalendar, 
  FeatherPlus,
  FeatherCheck,
  FeatherX
} from '../components/ui/icons';
import { leaveApi } from '../services/leaveService';
import { userApi } from '../services/api';
import { isManager } from '../utils/roleUtils';
import { useAuth } from '../context/AuthContext';
import LeaveBalanceCard from '../components/Leaves/LeaveBalanceCard';
import LeaveApplicationForm from '../components/Leaves/LeaveApplicationForm';
import LeaveApplicationList from '../components/Leaves/LeaveApplicationList';
import PendingApprovals from '../components/Leaves/PendingApprovals';
import './LeaveManagement.css';

const LeaveManagement = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [leaves, setLeaves] = useState([]);
  const [myLeaves, setMyLeaves] = useState([]);
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [balance, setBalance] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [error, setError] = useState(null);
  const [userIsManager, setUserIsManager] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    checkUserRole();
    fetchData();
  }, [selectedYear, userIsManager]);

  const checkUserRole = async () => {
    try {
      const { role } = await userApi.getRole();
      setUserIsManager(isManager(role));
    } catch (err) {
      console.error('Error checking user role:', err);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const currentYear = selectedYear;

      if (userIsManager) {
        // Managers see all leaves, pending approvals, their own leaves, and their own balance
        const [allLeavesData, pendingData, myLeavesData, balanceData] = await Promise.all([
          leaveApi.getAll(),
          leaveApi.getPending(),
          leaveApi.getMyLeaves(currentYear),
          leaveApi.getBalance(currentYear)
        ]);
        setLeaves(allLeavesData.leaves || []); // All leaves for the "All Leave Applications" section
        setPendingLeaves(pendingData.leaves || []);
        setBalance(balanceData.balance);
        // Store own leaves separately for the "My Leave Applications" section
        setMyLeaves(myLeavesData.leaves || []);
      } else {
        // Employees see their own leaves and balance
        const [myLeavesData, balanceData] = await Promise.all([
          leaveApi.getMyLeaves(currentYear),
          leaveApi.getBalance(currentYear)
        ]);
        setLeaves(myLeavesData.leaves || []);
        setBalance(balanceData.balance);
      }

      setError(null);
    } catch (err) {
      setError('Failed to load leave data');
      console.error('Error fetching leave data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyLeave = async (formData) => {
    try {
      await leaveApi.create(formData);
      setShowForm(false);
      await fetchData();
    } catch (err) {
      throw err; // Let the form handle the error
    }
  };

  const handleApprove = async (leaveId) => {
    if (!window.confirm('Are you sure you want to approve this leave application?')) {
      return;
    }

    try {
      setActionLoading(true);
      await leaveApi.approve(leaveId);
      await fetchData();
    } catch (err) {
      setError('Failed to approve leave');
      console.error('Error approving leave:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (leaveId, rejectionReason) => {
    try {
      setActionLoading(true);
      await leaveApi.reject(leaveId, rejectionReason);
      await fetchData();
    } catch (err) {
      setError('Failed to reject leave');
      console.error('Error rejecting leave:', err);
      throw err;
    } finally {
      setActionLoading(false);
    }
  };

  const getYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear - 1; i <= currentYear + 1; i++) {
      years.push(i);
    }
    return years;
  };

  // Helper function to filter leaves by year with proper type handling
  const filterLeavesByYear = (leavesArray, year) => {
    if (!leavesArray || !Array.isArray(leavesArray)) {
      return [];
    }
    
    const targetYear = Number(year);
    
    return leavesArray.filter(leave => {
      // If year field exists, use it
      if (leave.year != null) {
        return Number(leave.year) === targetYear;
      }
      
      // Fallback: derive year from start_date if year field is missing
      if (leave.start_date) {
        const startDateYear = new Date(leave.start_date).getFullYear();
        return startDateYear === targetYear;
      }
      
      // If neither year nor start_date is available, exclude from results
      return false;
    });
  };

  if (loading) {
    return (
      <DefaultPageLayout>
        <div className="loading-container">
          <BunnyLoader 
            size="large" 
            text="Loading leave management..."
            animation="bounce"
          />
        </div>
      </DefaultPageLayout>
    );
  }

  return (
    <DefaultPageLayout>
      <div className="leave-management-container">
        {/* Header */}
        <div className="header">
          <div className="header-left">
            <div className="flex items-center gap-3">
              <IconWithBackground 
                icon={<FeatherCalendar />} 
                variant="brand"
              />
              <h1 className="text-heading-2">Leave Management</h1>
            </div>
          </div>
          <div className="header-right">
            <Button
              variant="primary"
              icon={<FeatherPlus />}
              onClick={() => setShowForm(true)}
            >
              Apply for Leave
            </Button>
          </div>
        </div>

        {/* Year Selector */}
        <div className="year-selector">
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="year-select"
          >
            {getYears().map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>

        {error && (
          <div className="error-message">
            <div className="error">{error}</div>
          </div>
        )}

        {/* Leave Balance (for all users) */}
        {balance && (
          <LeaveBalanceCard balance={balance} year={selectedYear} />
        )}

        {/* Pending Approvals (for managers) */}
        {userIsManager && (
          <PendingApprovals
            pendingLeaves={pendingLeaves}
            onApprove={handleApprove}
            onReject={handleReject}
            loading={actionLoading}
            currentUserId={user?.id}
          />
        )}

        {/* Leave Applications List */}
        {userIsManager && (
          <>
            <div className="leave-applications-section">
              <h2 className="section-title">My Leave Applications</h2>
              <LeaveApplicationList 
                leaves={filterLeavesByYear(myLeaves, selectedYear)} 
                showActions={false}
              />
            </div>
            <div className="leave-applications-section">
              <h2 className="section-title">All Leave Applications</h2>
              <LeaveApplicationList 
                leaves={filterLeavesByYear(leaves, selectedYear)} 
                showActions={false}
              />
            </div>
          </>
        )}
        {!userIsManager && (
          <div className="leave-applications-section">
            <h2 className="section-title">My Leave Applications</h2>
            <LeaveApplicationList 
              leaves={filterLeavesByYear(leaves, selectedYear)} 
              showActions={false}
            />
          </div>
        )}

        {/* Leave Application Form Modal */}
        {showForm && (
          <LeaveApplicationForm
            onSuccess={handleApplyLeave}
            onCancel={() => setShowForm(false)}
            year={selectedYear}
            balance={balance}
          />
        )}
      </div>
    </DefaultPageLayout>
  );
};

export default LeaveManagement;

